<template>
  <div
    ref="customRadioRef"
    :class="getCustomRadioCSSClasses"
  >
    <input
      :id="name"
      :checked="checked"
      :data-qa="dataQa"
      :data-qa-value="dataQaValue"
      :value="value"
      class="custom-radio__input"
      type="radio"
      @blur="handleBlurEvent"
      @change="handleChangeEvent"
      @click="handleClickEvent"
      @focus="handleFocusEvent"
    />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

defineOptions({
  inheritAttrs: false,
})

const props = defineProps({
  name: {
    type: String,
    default: '',
  },
  value: {
    type: String,
    default: '',
  },
  checked: {
    type: Boolean,
    default: false,
  },
  dataQa: {
    type: String,
    default: '',
  },
  dataQaValue: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['change', 'focus', 'blur'])

const focused = ref(false)

const customRadioRef = ref(null)

const getCustomRadioCSSClasses = computed(() => {
  const classes = ['custom-radio']

  if (props.checked) {
    classes.push('custom-radio--checked')
  } else if (focused.value) {
    classes.push('custom-radio--focused')
  } else {
    classes.push('custom-radio--default')
  }

  return classes
})

const handleChangeEvent = (event) => {
  const { checked, value } = event.target
  emit('change', {
    checked,
    value,
  })
}

const handleFocusEvent = () => {
  focused.value = true
  emit('focus')
}

const handleBlurEvent = () => {
  focused.value = false
  emit('blur')
}

const handleClickEvent = () => {
  customRadioRef.value.querySelector('.custom-radio__input').checked = true
}
</script>

<style lang="scss">
.quiz-radio {
  display: flex;
  align-items: center;
  background: $kds-color-background-subtle;
  border-radius: 8px;
  border: 1px solid $kds-color-background-strong;
  cursor: pointer;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;

  padding: 12px 16px;
  margin: 0 auto;

  @media (hover: hover) {
    &:hover {
      background-color: $kds-color-background-strong;
    }
  }

  &:focus-within {
    background-color: $kds-color-background-strong;
    outline: 2px solid $kds-color-line-focus;
    border: 1px solid $kds-color-line-focus;
  }

  &--checked {
    background: $kds-color-background-interactive-accent;
    color: $kds-color-text-primary-inverse;

    @media (hover: hover) {
      &:hover {
        background-color: $kds-color-background-interactive-accent-hover;
      }
    }

    &:focus-within {
      background-color: $kds-color-background-interactive-accent-hover;
      outline: 2px solid $kds-color-line-focus;
      border: 1px solid $kds-color-line-focus;
    }
  }

  &__label {
    width: 100%;
    cursor: pointer;
    box-sizing: border-box;
    user-select: none;
    padding-right: 16px;
  }
}

.quiz-radio__smile-layout,
.quiz-radio__join-layout,
.quiz-radio__sad-layout {
  margin: 0 16px;
  margin-left: 12px;
  width: 36px;
  height: 36px;

  .custom-radio {
    background: transparent;
    border: none;
    width: 100%;
    height: 100%;
  }
}

.quiz-radio__smile-layout {
  background: url(@shared/assets/img/b2b/smile.svg) center no-repeat;
  background-size: 100%;
}

.quiz-radio__join-layout {
  background: url(@shared/assets/img/b2b/join.svg) center no-repeat;
  background-size: 100%;
}

.quiz-radio__sad-layout {
  background: url(@shared/assets/img/b2b/sad.svg) center no-repeat;
  background-size: 100%;
}

.quiz-radio__smiles-layout {
  flex-direction: row-reverse;
  padding-left: 5px;
}

.custom-radio {
  position: relative;
  width: 20px;
  height: 20px;
  box-sizing: border-box;
  border-radius: 10px;
  background-color: #fff;

  &__input {
    position: absolute;
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }

  &--checked {
    background-color: #000;
    border: 5px solid #fff;
  }

  &--focused {
    box-shadow: 0 0 0 2px $kds-color-background-interactive-accent;
    border: 5px solid #fff;
  }
}
</style>
